import React, { useCallback, useMemo } from 'react';
import { Label, Message } from '@naf/input';
import {
  COUNTRY_DIAL_CODES,
  DEFAULT_COUNTRY_CODE,
  PhoneNumberValue,
  findCountryByCode,
} from '../../../lib/phoneValidation';
import {
  COUNTRY_DROPDOWN_MAX_LINES,
  COUNTRY_OPTIONS,
  COUNTRY_PLACEHOLDER,
  COUNTRY_SELECT_WIDTH,
  CountryOption,
  PHONE_LABEL_DEFAULT,
  PHONE_PLACEHOLDER_DEFAULT,
  toCompactOption,
} from './PhoneInputWithCountryCode.utils';
import { CountrySelect, FieldWrapper, InputRow, PhoneNumberInput } from './PhoneInputWithCountryCode.styles';

export interface PhoneInputWithCountryCodeProps {
  value?: PhoneNumberValue;
  onChange: (value: PhoneNumberValue) => void;
  errorMessage?: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  id?: string;
}

export const PhoneInputWithCountryCode = ({
  value,
  onChange,
  errorMessage,
  disabled,
  label = PHONE_LABEL_DEFAULT,
  placeholder = PHONE_PLACEHOLDER_DEFAULT,
  id,
}: PhoneInputWithCountryCodeProps) => {
  const country = findCountryByCode(value?.countryCode ?? DEFAULT_COUNTRY_CODE) ?? COUNTRY_DIAL_CODES[0];
  const selectedOption = useMemo(() => toCompactOption(country), [country]);

  const handleCountrySelect = useCallback(
    (option: CountryOption) => {
      if (option.value === selectedOption.value) return;
      onChange({ countryCode: option.value || DEFAULT_COUNTRY_CODE, number: value?.number ?? '' });
    },
    [selectedOption.value, value?.number, onChange],
  );

  const handleNumberChange = useCallback(
    (nextNumber: string) => {
      onChange({ countryCode: value?.countryCode || DEFAULT_COUNTRY_CODE, number: nextNumber });
    },
    [value?.countryCode, onChange],
  );

  return (
    <FieldWrapper>
      {label && <Label htmlFor={id}>{label}</Label>}
      <InputRow>
        <CountrySelect
          options={COUNTRY_OPTIONS}
          selected={selectedOption}
          handleSelect={handleCountrySelect}
          placeholder={COUNTRY_PLACEHOLDER}
          ariaLabel="Landkode"
          disabled={disabled}
          maxLines={COUNTRY_DROPDOWN_MAX_LINES}
          width={COUNTRY_SELECT_WIDTH}
        />
        <PhoneNumberInput
          id={id}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder={placeholder}
          value={value?.number ?? ''}
          onChange={handleNumberChange}
          error={!!errorMessage}
          disabled={disabled}
          width="100%"
        />
      </InputRow>
      {!!errorMessage && <Message error>{errorMessage}</Message>}
    </FieldWrapper>
  );
};
